import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    filtersSelector,
    navSelector
} from 'common/store/modules/competition-nav/selectors';
import { useGetMatchRosterRoundQuery } from 'common/store/modules/api/cfs-api';
import { useGetMatchesQuery } from 'common/store/modules/api/afl-api';
import { useSingleSiteSetting } from 'common/react/hooks/use-single-site-setting';
import { useGroupMatches } from 'common/react/hooks/use-group-matches';
import { useFilterMatches } from 'common/react/hooks/use-filter-matches';
import { EmptyState } from 'common/react/components/EmptyState';
import { filterByCity } from 'widgets/fixtures/js/utils/filter-by-city';
import { TeamLineupsItemWrapper } from './TeamLineupsItemWrapper';

export const TeamLineupsList = ({ competitionId, compSeason, roundNumber }) => {
    const nav = useSelector(navSelector);

    const { data: rosterData, isFetching: isRosterFetching } =
        useGetMatchRosterRoundQuery(
            { roundId: nav?.round.providerId },
            {
                skip: !nav?.round.providerId
            }
        );

    const filters = useSelector(filtersSelector);
    const selectedRegion = useSingleSiteSetting('region');

    const { matches = [], isMatchesFetching } = useGetMatchesQuery(
        {
            pageSize: 300,
            competitionId: competitionId,
            compSeasonId: compSeason.id,
            ...{ roundNumber: roundNumber },
            ...(filters.teams.length && { teamId: filters.teams }),
            ...(filters.venues.length && { venueId: filters.venues })
        },
        {
            selectFromResult: ({ data, isFetching }) => ({
                matches:
                    data && filters.cities.length
                        ? filterByCity(data, filters.cities)
                        : data,
                isMatchesFetching: isFetching
            })
        }
    );

    // Add index property for tracking
    const formattedMatches = matches.map((item, index) =>
        Object.assign({}, item, { index: index + 1 })
    );

    // Group matches by date
    const matchItems = useGroupMatches(
        useFilterMatches(formattedMatches),
        selectedRegion.timezone
    );

    const matchItemsWithRosters = useMemo(() => {
        if (!matchItems?.length || !rosterData?.length) {
            return null;
        }

        return matchItems.map((item) => {
            const found = rosterData.find(
                (rosterItem) => item.providerId === rosterItem.match?.matchId
            );

            if (found) {
                return {
                    ...item,
                    rosterItem: found
                };
            }
            return item;
        });
    }, [matchItems, rosterData]);

    if (isRosterFetching || isMatchesFetching) {
        return (
            <div className="stats-section-wrapper wrapper">
                <>Loading...</>
            </div>
        );
    }

    if (!matchItemsWithRosters) {
        return <EmptyState cssClass="competition-nav__empty-state" />;
    }

    return (
        <div className="stats-section-wrapper wrapper">
            {matchItemsWithRosters.map((item, index) => (
                <TeamLineupsItemWrapper
                    key={
                        typeof item === 'string' ? `${item}-${index}` : item.id
                    }
                    item={item}
                />
            ))}
        </div>
    );
};

TeamLineupsList.propTypes = {
    competitionId: PropTypes.number,
    compSeason: PropTypes.object,
    roundNumber: PropTypes.number
};
